import { Button } from "react-migration/components/DeprecatedButton";
import { TextInputV2 } from "react-migration/components/TextInputV2";
import { RadioGroup } from "react-migration/components/RadioGroup";
import { TransactionPurchaseType } from "react-migration/domains/ownership/typings/Transaction";
import { PaymentTypes, PurchaseType } from "../types";
import { formatToCurrency } from "react-migration/lib/util/numberFormat";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useAccountQuery } from "react-migration/domains/dashboard/topup/hooks/useAccountQuery";
import { TitleIcon } from "./TitleIcon";
import { setShowTopupModal } from "src/js/stores/navigation/actions";
import { PRICE_PER_TITLE_GBP } from "src/js/util/units_and_constants";

type PurchaseFormType = {
  onCancel: () => void;
  titlesToPurchase: string[];
  setSelectedStep: (v: number) => void;
  transactionPurchaseType: TransactionPurchaseType;
  purchaseType?: PurchaseType;
  paymentType: PaymentTypes;
  setPaymentType: (v: PaymentTypes) => void;
  hmlrUsername: string;
  setHmlrUsername: (v: string) => void;
  hmlrPassword: string;
  setHmlrPassword: (v: string) => void;
  handlePurchase: () => void;
};

export const PurchaseTitleModalPurchaseForm = ({
  onCancel,
  titlesToPurchase,
  setSelectedStep,
  transactionPurchaseType,
  purchaseType,
  paymentType,
  setPaymentType,
  hmlrUsername,
  setHmlrUsername,
  hmlrPassword,
  setHmlrPassword,
  handlePurchase,
}: PurchaseFormType) => {
  const { t } = useTranslation();
  const { data: accountData } = useAccountQuery();

  const accountBalance = () => {
    if (!accountData) return;
    return accountData.accountWithViews.land_reg_credit_pounds >= 0
      ? `${formatToCurrency({
          amount: accountData.accountWithViews.land_reg_credit_pounds,
          currency: "GBP",
        })}`
      : t("dashboard.overdrawn_by", {
          amount: `${formatToCurrency({
            amount: accountData.accountWithViews.land_reg_credit_pounds,
            currency: "GBP",
          })}`,
        });
  };

  return (
    <div data-testid="step-2-purchase" className="flex flex-col h-full mx-4">
      <div className="overflow-y-auto max-h-[400px] grow">
        <h2 className="my-6">Order</h2>
        <div className="flex">
          <div className="w-24 h-24 overflow-hidden border rounded-md border-border-action shrink-0">
            <TitleIcon className="object-cover object-center w-full h-full" />
          </div>
          <div className="flex flex-col flex-1 ml-4">
            <div>
              <div className="flex justify-between text-base font-medium text-text-base-secondary">
                <h3>
                  {transactionPurchaseType === TransactionPurchaseType.TITLE_PLAN_DOCUMENT
                    ? "Title Plan"
                    : "Title Register"}
                </h3>
                <p className="ml-4">
                  {formatToCurrency({
                    amount: titlesToPurchase.length * PRICE_PER_TITLE_GBP,
                    currency: "GBP",
                  })}
                </p>
              </div>
            </div>
            <div className="flex items-end justify-between flex-1 text-sm">
              <p className="text-text-base-tertiary">Qty {titlesToPurchase.length}</p>
            </div>
          </div>
        </div>

        <h3 className="my-6">Purchase with</h3>

        <RadioGroup defaultValue={PaymentTypes.Balance} onValueChange={(v) => setPaymentType(v as PaymentTypes)}>
          <label data-testid="purchase-credits" className="flex! items-center w-full mb-1">
            <RadioGroup.Item className="w-4! h-4! flex-non" value={PaymentTypes.Balance}>
              <RadioGroup.Indicator />
            </RadioGroup.Item>
            <div className="flex justify-between w-full text-text-base-secondary">
              <div className="pl-2">LandTech Balance</div>
              <div className="flex">
                {`Current balance: ${accountBalance()}`}{" "}
                <div className="ml-2 cursor-pointer text-text-base-active" onClick={() => setShowTopupModal(true)}>
                  Topup?
                </div>
              </div>
            </div>
          </label>
          <label data-testid="purchase-hmlr" className="flex! items-center mt-1 mb-2">
            <RadioGroup.Item className="w-4! h-4! flex-none text-xs text-text-base-secondary" value={PaymentTypes.HMLR}>
              <RadioGroup.Indicator />
            </RadioGroup.Item>
            <span className="pl-2">{"HM Land Registry Portal"}</span>
          </label>
        </RadioGroup>

        {paymentType === PaymentTypes.HMLR && (
          <div>
            <div className="flex flex-col gap-4 mt-2 md:flex-row">
              <div className="grow">
                <TextInputV2
                  label="HM Land Registry Username"
                  value={hmlrUsername}
                  onChange={(e) => setHmlrUsername(e.target.value)}
                />
              </div>
              <div className="grow">
                <TextInputV2
                  label="HM Land Registry Password"
                  type="password"
                  value={hmlrPassword}
                  onChange={(e) => setHmlrPassword(e.target.value)}
                />
              </div>
            </div>
            <div className="pt-2">
              By placing your order, you agree to{" "}
              <a
                href="https://www.gov.uk/government/publications/conditions-of-use-hm-land-registry-business-e-services"
                target="_blank"
                rel="noreferrer"
              >
                HM Land Registry’s Conditions of Use
              </a>
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end pt-4">
        <Button.Secondary onClick={() => onCancel()} data-testid="cancel-button" className="mr-2">
          {t("title_purchase.cancel")}
        </Button.Secondary>
        <Button.Secondary onClick={() => setSelectedStep(1)} data-testid="back-button" className="mr-2">
          {t("title_purchase.back")}
        </Button.Secondary>
        <Button.Primary
          disabled={
            (paymentType === PaymentTypes.HMLR && (!hmlrUsername || !hmlrPassword)) ||
            (paymentType === PaymentTypes.Balance &&
              accountData &&
              titlesToPurchase.length * 3 > accountData.accountWithViews.land_reg_credit_pounds)
          }
          onClick={() => handlePurchase()}
          type="button"
          data-testid="purchase-button"
        >
          {purchaseType === PurchaseType.Repurchase ? t("title_purchase.repurchase") : t("title_purchase.purchase")}
        </Button.Primary>
      </div>
    </div>
  );
};
